/**
 * Centralized TanStack Query key factory.
 *
 * Single source of truth for query keys. Prevents typos from silently
 * breaking cache invalidation when a key is referenced from more than one
 * file.
 *
 * Pattern follows the hierarchical factory described in the TanStack Query
 * docs / TkDodo's "Effective React Query Keys":
 *
 *   queryKeys.partner.users.all        → ['users']           - base, invalidates all
 *   queryKeys.partner.users.list('a')  → ['users', 'a']      - specific list
 *
 * All factory outputs are `as const` so the inferred tuple type narrows
 * correctly at call sites. Default `invalidateQueries({ queryKey })` matching
 * is prefix-based, so invalidating `all` will also invalidate every `list(...)`.
 *
 * When adding new keys: prefer this factory over inline literals. For purely
 * local keys (one file reads AND writes, no invalidation elsewhere), a local
 * constant is fine - but if in doubt, put it here.
 *
 * For complex filter objects (orders / deliveries infinite scroll) we expose
 * just the base prefix and let the hook spread its own filter values in. That
 * keeps this factory free of importing internal filter types while still
 * preventing the base string from drifting across files.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Roles = readonly unknown[];
type FoodTypeLike = unknown;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const queryKeys = {
  incidentMessages: {
    all: ['incident-messages'] as const,
  },

  /**
   * Conversations. Kept under one `'conversations'` prefix so a logout or a
   * socket reconnect can drop everything with a single prefix match.
   *
   * `unreadCount` is shared by both apps even though the response shape
   * differs per app - a given bundle only ever talks to one of them.
   *
   * `messages` and `detail` stringify the id on purpose: a route search param
   * arrives as a string and a socket payload as a number, and without this the
   * same conversation would end up under two separate cache entries. The
   * merchant app has exactly one thread and no id for it, so it keys on the
   * `USER_CONVERSATION_ID` sentinel instead.
   */
  conversations: {
    all: ['conversations'] as const,
    unreadCount: ['conversations', 'unread-count'] as const,
    /** Each scope, search and filter combination caches separately. */
    inbox: (
      /** The types, already joined - the scope is part of the identity. */
      types: string | null,
      query: string | null,
      archived: boolean,
      unansweredOnly: boolean,
      unreadOnly: boolean
    ) =>
      [
        'conversations',
        'inbox',
        types,
        query,
        archived,
        unansweredOnly,
        unreadOnly,
      ] as const,
    /** Prefix for every inbox variant - use to reset them all at once. */
    inboxAll: ['conversations', 'inbox'] as const,
    messages: (conversationId: number | string) =>
      ['conversations', 'messages', String(conversationId)] as const,
    /** Prefix for every thread's history - use to drop them all at once. */
    messagesAll: ['conversations', 'messages'] as const,
    /** Members, title and image - re-fetched on `conversation_updated`. */
    detail: (conversationId: number | string) =>
      ['conversations', 'detail', String(conversationId)] as const,
    /**
     * The caller's Viktor thread as an inbox row - id, preview, unread. Its
     * own key rather than an `inbox` variant: it comes from an idempotent
     * POST, not the list endpoint, and the socket patches it in place.
     */
    viktor: ['conversations', 'viktor'] as const,
    /**
     * The merchant app's Groups tab, per search. Its own key rather than an
     * `inbox` variant: a different endpoint with a different row shape, and
     * the socket patches it by hand where the admin inbox only goes stale.
     */
    groups: (query: string | null) =>
      ['conversations', 'groups', query] as const,
    /** Prefix for every search of that list. */
    groupsAll: ['conversations', 'groups'] as const,
    /**
     * Locations offered by the merchant group member picker's filter. Under
     * this prefix rather than the locations lists: it is a short search the
     * picker owns, and the socket's logout and reconnect resets go with it.
     */
    candidateLocations: (query: string | null) =>
      ['conversations', 'candidate-locations', query] as const,
    /** People you can start a conversation with, per pool. */
    candidates: (
      query: string | null,
      pool: string,
      supportAudience: string | null,
      locationId: number | null = null
    ) =>
      [
        'conversations',
        'candidates',
        query,
        pool,
        supportAudience,
        locationId,
      ] as const,
  },

  /** Permissions of the logged in user (admin app only). */
  permissions: {
    me: ['permissions', 'me'] as const,
    byUser: (userId: number | string) =>
      ['permissions', String(userId)] as const,
  },

  tracking: {
    all: ['tracking'] as const,
    /* The merchant's view is a different answer for the same link. */
    byUid: (uid: string, source?: string | null) =>
      ['tracking', uid, source ?? null] as const,
    games: (uid: string) => ['tracking', uid, 'games'] as const,
  },

  /**
   * Geoapify routing between a pickup and a dropoff. Keyed on the numbers, not
   * on the objects carrying them: delivery payloads are replaced on every poll,
   * and a key built from object identity would fetch an unchanged route again.
   */
  geoapify: {
    route: (
      pickupLat: number,
      pickupLng: number,
      dropoffLat: number,
      dropoffLng: number
    ) =>
      ['geoapify-route', pickupLat, pickupLng, dropoffLat, dropoffLng] as const,
  },

  /**
   * Tax rates of one location. Read by the admin location screen and by the
   * menu's tax pickers, which is why the key is here and not local to either:
   * assigning a tax on an item must be able to invalidate the list the
   * location screen is showing.
   */
  taxRates: {
    all: ['tax-rates'] as const,
    byLocation: (locationId: number | string) =>
      ['tax-rates', String(locationId)] as const,
  },

  /** A location's own brands (`/v2/locations/:id/brands/own`). */
  ownBrands: {
    all: ['own-brands'] as const,
    byLocation: (locationId: number | string) =>
      ['own-brands', String(locationId)] as const,
  },

  brandTemplates: {
    all: ['brandTemplates'] as const,
    list: (query: string | null) => ['brandTemplates', query] as const,
  },

  brandOwner: {
    /** Single advanced-query-types lookup; no parameters. */
    advancedQueryTypes: ['brand-owner-advanced-query-types'] as const,
  },

  /**
   * Admin & partner orders infinite scroll. The hook spreads filter values
   * into its key after the base - keep the base centralized so a rename of
   * `'orders-infinite'` can't drift between files.
   */
  orders: {
    all: ['orders-infinite'] as const,
    /** Companion count + sales query that pairs with the infinite list. */
    countSalesAll: ['orders-infinite-count'] as const,
  },

  /** Admin & partner deliveries infinite scroll. Same pattern as `orders`. */
  deliveries: {
    all: ['deliveries-infinite'] as const,
  },

  /**
   * Wallet / earnings, shared by both apps.
   *
   * `earningExplanation` is the AI explanation of one order's payout. The hook
   * pins staleTime/gcTime to Infinity, so this key doubles as the per-session
   * cache: reopening an order never re-fires the slow (~3-8s) generation call.
   *
   * `userId` is the wallet owner on admin, where support can browse several
   * merchants' wallets in one session - the same orderId must not share text
   * (or a cached 404) across wallets. The partner endpoint is token-scoped, so
   * partner callers pass null and share the `'me'` slot. Ids are stringified
   * because they arrive as route-param strings on admin and as numbers off the
   * transaction row.
   */
  wallet: {
    all: ['wallet'] as const,
    earningExplanation: (
      userId: number | string | null,
      orderId: number | string
    ) =>
      [
        'wallet',
        'earning-explanation',
        userId == null ? 'me' : String(userId),
        String(orderId),
      ] as const,
  },

  /**
   * Integration Access: the POS and marketplaces a partner told us they use and
   * granted us access to. Keyed by route family as well as id, because an
   * IncompleteLocation and the Location it becomes on `complete` are separate
   * rows that can share a number.
   */
  integrationAccess: {
    all: ['integration-access'] as const,
    detail: (
      scope:
        | { incompleteLocationId: number | string }
        | { locationId: number | string }
    ) =>
      'incompleteLocationId' in scope
        ? ([
            'integration-access',
            'incomplete',
            String(scope.incompleteLocationId),
          ] as const)
        : ([
            'integration-access',
            'location',
            String(scope.locationId),
          ] as const),
  },

  /**
   * What a location owes vGrubs, merchant side (`/v1/outstanding-balance`).
   *
   * The id is stringified so a route param and a number share one slot. A
   * payment invalidates `all` rather than its own location: one vDrive balance
   * can be shared by several of the owner's locations, and paying from one
   * changes what the others owe too.
   */
  outstandingBalance: {
    all: ['/v1/outstanding-balance'] as const,
    location: (locationId: number | string) =>
      ['/v1/outstanding-balance', String(locationId)] as const,
  },

  /** Partner-app scoped keys. */
  partner: {
    users: {
      all: ['users'] as const,
      list: (query: string | null) => ['users', query] as const,
    },
    locations: {
      all: ['/v1/locations'] as const,
      list: (query: string | null) => ['/v1/locations', query] as const,
      /** Every verified location at once - what the billing notices walk. */
      verifiedAll: ['/v1/locations', 'verified-all'] as const,
      /** The license image URL Settings shows, null when none was uploaded. */
      businessLicense: (locationId: number | string) =>
        ['/v1/locations', String(locationId), 'business-license'] as const,
    },
    brands: {
      all: ['brands'] as const,
      list: (
        query: string | null,
        foodType: FoodTypeLike,
        locationId: number
      ) => ['brands', query, foodType, locationId] as const,
    },
  },

  /**
   * Support tickets - the AI triage bot's output, worked from the admin app.
   *
   * `listAll` is what the socket invalidates on every ticket event: the tabs
   * differ only by server-side filters, and which of them a changed ticket now
   * belongs to is not something the client can work out (the "Mine" tab filters
   * on `claimedById`, while the event carries only the claimant's name).
   *
   * `openCount` is written straight from the socket payload and by every list
   * response, which carries the same number - so the badge is never a request
   * of its own beyond the first.
   */
  supportTickets: {
    all: ['/v1/support-tickets'] as const,
    listAll: ['/v1/support-tickets', 'list'] as const,
    list: (
      status: string | null,
      type: string | null,
      claimedById: number | null,
      locationId: number | null
    ) =>
      [
        '/v1/support-tickets',
        'list',
        status,
        type,
        claimedById,
        locationId,
      ] as const,
    detail: (ticketId: number | string) =>
      ['/v1/support-tickets', 'detail', String(ticketId)] as const,
    /** The order picker's list, per ticket and per search term. */
    orderCandidates: (ticketId: number | string, query: string) =>
      [
        '/v1/support-tickets',
        'order-candidates',
        String(ticketId),
        query,
      ] as const,
    /** The ticket's menu price fixes. Refetched on every event for it. */
    menuPriceFixes: (ticketId: number | string) =>
      ['/v1/support-tickets', 'menu-price-fixes', String(ticketId)] as const,
    openCount: ['/v1/support-tickets', 'open-count'] as const,
  },

  /**
   * What the backend noticed on its own. One prefix for the v2 Dashboard card
   * and the v1 list, so a socket reconnect can refresh both with one call.
   */
  systemProblems: {
    all: ['/v1/system-problems'] as const,
    listAll: ['/v1/system-problems', 'list'] as const,
    /** Positions matter: `useSystemProblemsSocket` reads the filters back. */
    list: (
      status: string | null,
      locationId: number | null,
      severity: string | null,
      category: string | null
    ) =>
      [
        '/v1/system-problems',
        'list',
        status,
        locationId,
        severity,
        category,
      ] as const,
    detail: (problemId: number | string) =>
      ['/v1/system-problems', 'detail', String(problemId)] as const,
    /** Every Orders tab card query, whichever category chip is picked. */
    dashboard: ['/v1/system-problems', 'dashboard'] as const,
    /** The card for one chip; `null` is All. */
    dashboardFor: (category: string | null) =>
      ['/v1/system-problems', 'dashboard', category] as const,
    /** A location's tablet printers (Location Details, printerError). */
    locationPrinters: (locationId: number | string) =>
      ['/v1/system-problems', 'location-printers', String(locationId)] as const,
    /** Restaurant phones for tabletOffline problems, per set of locations. */
    locationPhones: (locationIds: number[]) =>
      ['/v1/system-problems', 'location-phones', ...locationIds] as const,
  },

  /** Location Details: what the client made with us, and its alerts. */
  locationInsights: {
    /** Every location's alerts: a problem changing may touch any of them. */
    alertsAll: ['location-insights', 'alerts'] as const,
    alerts: (locationId: number | string) =>
      ['location-insights', 'alerts', String(locationId)] as const,
    /** All time: the cards do not follow the Overview's dates. */
    serviceValue: (locationId: number | string) =>
      ['location-insights', 'service-value', String(locationId)] as const,
  },

  /**
   * Restaurant-to-restaurant referrals. One prefix for both apps, so a push
   * about a referral can refresh whatever referral screen is open without
   * knowing which app it is in.
   */
  restaurantReferrals: {
    all: ['/v1/referrals'] as const,
    /** Admin: the program, per status filter and search. */
    list: (status: string | null, query: string) =>
      ['/v1/referrals', 'list', status, query] as const,
    detail: (referralId: number | string) =>
      ['/v1/referrals', 'detail', String(referralId)] as const,
    /** Merchant: one location's invites. */
    merchant: (locationId: number) =>
      ['/v1/referrals', 'merchant', locationId] as const,
    /** Merchant: the verified locations that can do the inviting. */
    referringLocations: ['/v1/referrals', 'referring-locations'] as const,
    /** Admin Location Details: referred by, and how many it referred. */
    locationSummary: (locationId: number | string) =>
      ['/v1/referrals', 'location-summary', String(locationId)] as const,
  },

  /**
   * Which locations the chat router runs for. Its own group rather than a
   * corner of `supportTickets`: the socket drops that whole prefix on every
   * reconnect, and this list has nothing to do with a ticket changing.
   */
  chatTriage: {
    pilotLocations: ['/v1/chat-triage/pilot-locations'] as const,
  },

  /**
   * Menu Health reports of one location. The socket patches rows in place, so
   * the id is stringified: a route param arrives as a string and a prop as a
   * number, and both must land on the same entry.
   */
  menulytics: {
    reports: (locationId: string | number) =>
      ['/v2/locations/menulytics-reports', String(locationId)] as const,
  },

  /**
   * What's New history, for whichever app + platform the `app-version` header
   * says this build is - one list per bundle, so the base is the whole key.
   */
  announcementsHistory: {
    all: ['/v1/announcements/history'] as const,
  },

  /**
   * The bell. The same routes in both apps, and a bundle only ever talks to
   * one of them, so no app goes in the key - the USER does: signing out is a
   * route change, not a reload, and without the id a colleague signing in on
   * the same tab would be shown the previous person's list from cache.
   *
   * One list per `unreadOnly`, shared by the dropdown and the full page: the
   * dropdown shows the head of the same first page, so See all costs nothing.
   */
  notifications: {
    all: ['/v1/notifications'] as const,
    unreadCount: (userId: number) =>
      ['/v1/notifications', userId, 'unread-count'] as const,
    /** Prefix for both lists - the socket patches or refetches them together. */
    listAll: (userId: number) => ['/v1/notifications', userId, 'list'] as const,
    list: (userId: number, unreadOnly: boolean) =>
      ['/v1/notifications', userId, 'list', unreadOnly] as const,
  },

  /** Admin-app scoped keys. */
  admin: {
    /**
     * The announcements list. Only the base lives here: the hook spreads its
     * own filters after it, and the wizard and the delete both refresh the
     * list by invalidating this prefix.
     */
    announcements: {
      all: ['/v1/announcements'] as const,
      /** Everything below sits under `all`, so the wizard's save refreshes it. */
      detail: (id: number) => ['/v1/announcements', id] as const,
      viewers: (id: number, filters: object) =>
        ['/v1/announcements', id, 'viewers', filters] as const,
      viewsPerDay: (id: number, days: number) =>
        ['/v1/announcements', id, 'views-per-day', days] as const,
      activity: (id: number) => ['/v1/announcements', id, 'activity'] as const,
    },
    /**
     * Receivables. Every write answers with the location's whole detail, which
     * goes straight into `location`; the list is invalidated alongside, since
     * its totals move with it.
     */
    receivables: {
      all: ['/v1/finances/receivables'] as const,
      /** Every filter of the list; invalidating this refreshes them all. */
      list: ['/v1/finances/receivables', 'list'] as const,
      listBy: (filter: string) =>
        ['/v1/finances/receivables', 'list', filter] as const,
      /** The Finances tab card, per range. */
      overview: (params: {
        from: string;
        to: string;
        timeZone: string;
        groupByType: string;
      }) => ['/v1/finances/receivables', 'overview', params] as const,
      location: (locationId: number | string) =>
        ['/v1/finances/receivables', 'location', String(locationId)] as const,
      /** Every repayment of a location, paged, for the history modal. */
      repayments: (locationId: number | string) =>
        ['/v1/finances/receivables', 'repayments', String(locationId)] as const,
      /** The call summary, fetched fresh each time its modal opens. */
      collectionLetterSummary: (locationId: number | string) =>
        [
          '/v1/finances/receivables',
          'collection-letter-summary',
          String(locationId),
        ] as const,
      /** The detail answer carries no name, so it is looked up on its own. */
      locationName: (locationId: number | string) =>
        [
          '/v1/finances/receivables',
          'location-name',
          String(locationId),
        ] as const,
    },
    /** One AI strategy suggestion job, polled until it is done. */
    vDriveStrategySuggestion: (scope: string, id: string, jobId: string) =>
      ['/v1/vdrive', scope, id, 'strategy-suggestion', jobId] as const,
    /** Finances tab: no SaaS fee yet, but would qualify. Last 30 days. */
    saasEligibleLocations: ['/v1/finances/saas-eligible-locations'] as const,
    /** Finances tab: what "Monthly Saas" is made of, per range. */
    saasDetails: {
      all: ['/v1/finances/saas-details'] as const,
      summary: (range: { from: string; to: string; timeZone: string }) =>
        ['/v1/finances/saas-details', 'summary', range] as const,
      payments: (
        range: { from: string; to: string; timeZone: string },
        status: string
      ) => ['/v1/finances/saas-details', 'payments', range, status] as const,
    },
    /** Finances tab: the MyChips games, per range. */
    financesGames: {
      all: ['/v1/finances/games'] as const,
      summary: (range: { from: string; to: string; timeZone: string }) =>
        ['/v1/finances/games', 'summary', range] as const,
      players: (range: { from: string; to: string; timeZone: string }) =>
        ['/v1/finances/games', 'players', range] as const,
    },
    /** Finances tab: where "Service Fee" came from, per range. */
    serviceFeeDetails: (range: {
      from: string;
      to: string;
      timeZone: string;
    }) => ['/v1/finances/service-fee-details', range] as const,
    verifiedLocations: {
      all: ['/v1/locations/verified'] as const,
      list: (query: string | null, deleted: boolean) =>
        ['/v1/locations/verified', query, deleted] as const,
      countAll: (query: string | null) =>
        ['/v1/locations/verified', 'count-all', query] as const,
    },
    // AI image library - global (search-only key; no brand/location scope).
    menuAiImages: {
      all: ['/v1/menu-ai/images'] as const,
      list: (search: string) => ['/v1/menu-ai/images', search] as const,
    },
    users: {
      all: ['/v1/admin/users'] as const,
      list: (query: string | null, roles: Roles, deleted: boolean) =>
        ['/v1/admin/users', query, roles, deleted] as const,
      countAll: (query: string | null) =>
        ['/v1/admin/users', 'count-all', query] as const,
    },
    auditLogs: {
      all: ['/v1/audit-logs'] as const,
      list: (query: string | null, from: string, to: string) =>
        ['/v1/audit-logs', query, from, to] as const,
    },
    /**
     * The four verification queues. Each is a flat one-element key because
     * the hooks don't currently parameterize them; consumers in
     * `*VerificationDetails` invalidate them by passing this tuple directly
     * to `queryClient.invalidateQueries({ queryKey })`.
     *
     * `count` is the sidebar badge query (`useVerificationsCount`) -
     * invalidated by each detail page after a verify/reject action so the
     * sidebar updates immediately.
     */
    verifications: {
      vDrive: ['/v1/vdrive'] as const,
      agents: ['/v1/agents/all'] as const,
      brands: ['/v1/brands/unverified'] as const,
      restaurants: ['/v1/locations/unverified'] as const,
      count: ['/v1/locations/verification-count'] as const,
    },
  },
} as const;

/**
 * vDrive partner console (apps/deliveries). Everything is rooted `['vd']` and
 * organization-scoped keys carry the orgId right after the domain, so one
 * `invalidateQueries({ queryKey: vdKeys.all })` after a socket reconnect
 * refreshes the whole console and switching organizations addresses a
 * different cache slice. Ids are stringified so a numeric and a string form
 * of the same id land on the same key.
 */
const vdId = (id: number | string | null | undefined) => String(id ?? '');

export const vdKeys = {
  all: ['vd'] as const,
  /** `GET /v1/me`: the user plus the organizations with the caller's role. */
  me: (userId: number | string | null | undefined) =>
    ['vd', 'me', vdId(userId)] as const,
  orgs: (userId: number | string | null | undefined) =>
    ['vd', 'orgs', vdId(userId)] as const,
  drivers: (orgId: number | string) => ['vd', 'drivers', vdId(orgId)] as const,
  /**
   * The unfiltered roster (the Drivers screen's "All" tab, driver names, the
   * Verifications count): one cache entry every reader shares.
   */
  driverRoster: (orgId: number | string) =>
    ['vd', 'drivers', vdId(orgId), 'list', 'all', ''] as const,
  driverDetail: (orgId: number | string, driverId: number | string) =>
    ['vd', 'drivers', vdId(orgId), vdId(driverId)] as const,
  /** Every page of the driver's Earnings tab. */
  driverEarnings: (orgId: number | string, driverId: number | string) =>
    ['vd', 'drivers', vdId(orgId), vdId(driverId), 'earnings'] as const,
  zones: (orgId: number | string) => ['vd', 'zones', vdId(orgId)] as const,
  users: (orgId: number | string) => ['vd', 'users', vdId(orgId)] as const,
  deliveries: (orgId: number | string) =>
    ['vd', 'deliveries', vdId(orgId)] as const,
  deliveryDetail: (orgId: number | string, deliveryId: number | string) =>
    ['vd', 'deliveries', vdId(orgId), vdId(deliveryId)] as const,
  live: (orgId: number | string) => ['vd', 'live', vdId(orgId)] as const,
  heatmap: (orgId: number | string) =>
    ['vd', 'live', vdId(orgId), 'heatmap'] as const,
  /** Everything under the wallet: invalidate this after money moved. */
  walletAll: (orgId: number | string) => ['vd', 'wallet', vdId(orgId)] as const,
  /** The card (`GET .../wallet`); money actions answer with it. */
  wallet: (orgId: number | string) =>
    ['vd', 'wallet', vdId(orgId), 'card'] as const,
  walletEntries: (orgId: number | string, kind: string) =>
    ['vd', 'wallet', vdId(orgId), 'entries', kind] as const,
  settlements: (orgId: number | string) =>
    ['vd', 'wallet', vdId(orgId), 'settlements'] as const,
  settlement: (orgId: number | string, settlementId: number | string) =>
    ['vd', 'wallet', vdId(orgId), 'settlements', vdId(settlementId)] as const,
  transfers: (orgId: number | string, kind: string) =>
    ['vd', 'wallet', vdId(orgId), 'transfers', kind] as const,
  transfer: (orgId: number | string, transferId: number | string) =>
    ['vd', 'wallet', vdId(orgId), 'transfer', vdId(transferId)] as const,
  driversOwed: (orgId: number | string) =>
    ['vd', 'wallet', vdId(orgId), 'drivers'] as const,
  driverPayouts: (orgId: number | string, driverId: number | string | null) =>
    ['vd', 'wallet', vdId(orgId), 'driver-payouts', vdId(driverId)] as const,
  /** Everything under Invoices: refetch this after a payment or a bell line. */
  invoicesAll: (orgId: number | string) =>
    ['vd', 'invoices', vdId(orgId)] as const,
  /** The tab header, the sidebar count and the lock screen. */
  invoiceSummary: (orgId: number | string) =>
    ['vd', 'invoices', vdId(orgId), 'summary'] as const,
  invoiceList: (orgId: number | string, tab: string) =>
    ['vd', 'invoices', vdId(orgId), 'list', tab] as const,
  invoiceDetail: (orgId: number | string, invoiceId: number | string) =>
    ['vd', 'invoices', vdId(orgId), 'detail', vdId(invoiceId)] as const,
  invoiceDeliveries: (orgId: number | string, invoiceId: number | string) =>
    ['vd', 'invoices', vdId(orgId), 'deliveries', vdId(invoiceId)] as const,
  notifications: (orgId: number | string) =>
    ['vd', 'notifications', vdId(orgId)] as const,
  /** The bell: its first page, and the dot on its own. */
  notificationsList: (orgId: number | string) =>
    ['vd', 'notifications', vdId(orgId), 'list'] as const,
  notificationsUnread: (orgId: number | string) =>
    ['vd', 'notifications', vdId(orgId), 'unread'] as const,
  notificationSettings: (orgId: number | string) =>
    ['vd', 'notifications', vdId(orgId), 'settings'] as const,
  dashboard: (orgId: number | string) =>
    ['vd', 'dashboard', vdId(orgId)] as const,
  shifts: (orgId: number | string) => ['vd', 'shifts', vdId(orgId)] as const,
  incidents: (orgId: number | string) =>
    ['vd', 'incidents', vdId(orgId)] as const,
  /** Every page of every filter of the incidents list. */
  incidentLists: (orgId: number | string) =>
    ['vd', 'incidents', vdId(orgId), 'list'] as const,
  incidentDetail: (orgId: number | string, incidentId: number | string) =>
    ['vd', 'incidents', vdId(orgId), vdId(incidentId)] as const,
  audit: (orgId: number | string) => ['vd', 'audit', vdId(orgId)] as const,
  integrations: (orgId: number | string) =>
    ['vd', 'integrations', vdId(orgId)] as const,
  /** A New Delivery's price from the price list, per pickup and dropoff. */
  deliveryPrice: (
    orgId: number | string,
    pickup: { lat: number; lng: number },
    dropoff: { lat: number; lng: number }
  ) =>
    [
      'vd',
      'integrations',
      vdId(orgId),
      'price',
      pickup.lat,
      pickup.lng,
      dropoff.lat,
      dropoff.lng,
    ] as const,
  /** The driver chat: inbox rows per search and filter, and the badge count. */
  chatInboxAll: (orgId: number | string) =>
    ['vd', 'chat', vdId(orgId), 'inbox'] as const,
  chatInbox: (orgId: number | string, query: string, unreadOnly: boolean) =>
    ['vd', 'chat', vdId(orgId), 'inbox', query, unreadOnly] as const,
  chatUnread: (orgId: number | string) =>
    ['vd', 'chat', vdId(orgId), 'unread'] as const,
};
